import { useState, useEffect, useMemo } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { CopyButton } from '@/components/CopyButton';
import { useUser } from '@/contexts/UserContext';
import { useToast } from '@/hooks/useToast';
import { filterByWarehouse } from '@/lib/warehouseFilter';
import { useTrips } from '@/hooks/useTrips';
import { useWarehouses } from '@/hooks/useWarehouses';
import { useShipments } from '@/hooks/useShipments';
import { useCustomers } from '@/hooks/useCustomers';
import { useInvoices } from '@/hooks/useInvoices';
import { MultiItemShipmentForm } from '@/components/shipments/MultiItemShipmentForm';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Truck,
  Ship,
  Plane,
  Plus,
  Search,
  Package,
  Weight,
  Box,
  MapPin,
  Calendar,
  ChevronDown,
  ChevronUp,
  Printer,
  FileText,
  Download,
  Edit,
  Trash2,
  Filter,
  Clock,
  CheckCircle,
  Target,
  LayoutGrid,
  List,
  Users,
  Receipt
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface TripsProps {
  filter?: string;
}

interface ShipmentItem {
  id: string;
  itemNumber: number;
  description: string;
  quantity: number;
  quantityUnit: string;
  weight: number;
  volume: number;
  boxes: number;
}

interface TripShipment {
  shipmentNumber: string;
  customerCode: string;
  customerName: string;
  description: string;
  weight: number;
  volume: number;
  packages: number;
  status: string;
  items?: ShipmentItem[];
}

interface Trip {
  id: string;
  name: string;
  originWarehouse: string;
  destinationWarehouse: string;
  status: 'Planning' | 'In Transit' | 'At Destination' | 'Completed';
  freightMethod: 'Air' | 'Sea';
  shipments: TripShipment[];
  totalWeight: number;
  totalVolume: number;
  totalPackages: number;
  departureDate: string;
  estimatedArrival: string;
  driver: string;
  vehicle: string;
}

interface TripsProps {
  filter?: 'all' | 'planning' | 'in-transit' | 'at-destination' | 'completed';
}

export default function Trips(props: TripsProps) {
  const { user } = useUser();
  const { filter = 'all' } = props;
  const { toast } = useToast();

  // Get warehouses from localStorage
  const [allWarehouses, setAllWarehouses] = useState<any[]>(() => {
    const saved = localStorage.getItem('shiphub-warehouses');
    const parsed = saved ? JSON.parse(saved) : [];
    return parsed;
  });

  const { trips: apiTrips, isLoading: tripsLoading, createTrip, updateTrip, deleteTrip } = useTrips();
  const { warehouses: apiWarehouses } = useWarehouses();
  const { shipments: apiShipments, updateShipment, createShipment, isCreating: isCreatingShipment } = useShipments();
  const { customers: apiCustomers } = useCustomers();
  const { createInvoice, isCreating: isCreatingInvoice } = useInvoices();

  const allTrips = apiTrips || [];

  // Filter warehouses based on user's access
  const warehouses = useMemo(() => {
    const allWarehouses = apiWarehouses || [];
    if (user?.role === 'admin') {
      return allWarehouses;
    }
    if (user?.role === 'warehouse' && user?.warehouseCode) {
      return allWarehouses;
    }
    return [];
  }, [apiWarehouses, user]);

  // Old localStorage state - keeping for compatibility
  const [, setAllTrips] = useState<Trip[]>(() => {
    return [
      {
        id: '1',
        name: 'GUANGZHOU-MOROCCO-001',
        originWarehouse: 'GUANGZHOU',
        destinationWarehouse: 'MOROCCO',
        status: 'Planning',
        freightMethod: 'Sea',
        shipments: [],
        totalWeight: 0,
        totalVolume: 0,
        totalPackages: 0,
        departureDate: '2024-01-18',
        estimatedArrival: '2024-01-23',
        driver: 'Sarah Driver',
        vehicle: 'TRK-002',
        createdAt: '2024-01-10'
      },
      {
        id: '2',
        name: 'GUANGZHOU-USA-002',
        originWarehouse: 'GUANGZHOU',
        destinationWarehouse: 'USA',
        status: 'In Transit',
        freightMethod: 'Air',
        shipments: [],
        totalWeight: 0,
        totalVolume: 0,
        totalPackages: 0,
        departureDate: '2024-01-16',
        estimatedArrival: '2024-01-18',
        driver: 'Mike Driver',
        vehicle: 'PLN-001',
        createdAt: '2024-01-12'
      },
      {
        id: '3',
        name: 'USA-MOROCCO-003',
        originWarehouse: 'USA',
        destinationWarehouse: 'MOROCCO',
        status: 'At Destination',
        freightMethod: 'Sea',
        shipments: [],
        totalWeight: 0,
        totalVolume: 0,
        totalPackages: 0,
        departureDate: '2024-01-14',
        estimatedArrival: '2024-01-20',
        driver: 'John Driver',
        vehicle: 'TRK-003',
        createdAt: '2024-01-13'
      },
      {
        id: '4',
        name: 'MOROCCO-USA-004',
        originWarehouse: 'MOROCCO',
        destinationWarehouse: 'USA',
        status: 'Completed',
        freightMethod: 'Air',
        shipments: [],
        totalWeight: 0,
        totalVolume: 0,
        totalPackages: 0,
        departureDate: '2024-01-10',
        estimatedArrival: '2024-01-15',
        driver: 'Emma Driver',
        vehicle: 'PLN-002',
        createdAt: '2024-01-11'
      }
    ];
  });

  // Filter trips based on user's warehouse access
  const trips = useMemo(() => {
    const filtered = filterByWarehouse(allTrips, user);
    return filtered;
  }, [allTrips, user]);

  // For debugging - show all trips regardless of filter
  const allTripsForDebug = allTrips;

  const [expandedTrips, setExpandedTrips] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [freightFilter, setFreightFilter] = useState<string>('all');
  const [warehouseFilter, setWarehouseFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  // Save trips to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('shiphub-trips', JSON.stringify(allTrips));
  }, [allTrips]);

  // Update warehouses when localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('shiphub-warehouses');
      setAllWarehouses(saved ? JSON.parse(saved) : []);
    };

    window.addEventListener('storage', handleStorageChange);

    // Also update immediately on mount
    const saved = localStorage.getItem('shiphub-warehouses');
    if (saved) {
      setAllWarehouses(JSON.parse(saved));
    }

    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingTrip, setEditingTrip] = useState<Trip | null>(null);
  const [selectedTripForPrint, setSelectedTripForPrint] = useState<Trip | null>(null);
  const [isPackingListDialogOpen, setIsPackingListDialogOpen] = useState(false);
  const [isPerformaInvoiceDialogOpen, setIsPerformaInvoiceDialogOpen] = useState(false);
  
  // New states for trip actions
  const [isCustomerSummaryOpen, setIsCustomerSummaryOpen] = useState(false);
  const [selectedTripForSummary, setSelectedTripForSummary] = useState<Trip | null>(null);
  const [expandedCustomers, setExpandedCustomers] = useState<Set<string>>(new Set());
  const [isCreateShipmentOpen, setIsCreateShipmentOpen] = useState(false);
  const [isCreateInvoiceOpen, setIsCreateInvoiceOpen] = useState(false);
  const [selectedTripForShipment, setSelectedTripForShipment] = useState<Trip | null>(null);
  const [selectedTripForInvoice, setSelectedTripForInvoice] = useState<Trip | null>(null);
  
  // Invoice form state
  const [selectedCustomerForInvoice, setSelectedCustomerForInvoice] = useState<string>('');
  const [selectedShipmentsForInvoice, setSelectedShipmentsForInvoice] = useState<Set<string>>(new Set());
  const [invoiceRate, setInvoiceRate] = useState<string>('10');
  const [invoiceDiscount, setInvoiceDiscount] = useState<string>('0');
  
  const [newTrip, setNewTrip] = useState({
    name: '',
    originWarehouse: '',
    destinationWarehouse: '',
    freightMethod: 'Air' as 'Air' | 'Sea',
    departureDate: ''
  });

  const toggleTripExpansion = (tripId: string) => {
    const newExpanded = new Set(expandedTrips);
    if (newExpanded.has(tripId)) {
      newExpanded.delete(tripId);
    } else {
      newExpanded.add(tripId);
    }
    setExpandedTrips(newExpanded);
  };

  // Toggle customer expansion in summary
  const toggleCustomerExpansion = (customerCode: string) => {
    const newExpanded = new Set(expandedCustomers);
    if (newExpanded.has(customerCode)) {
      newExpanded.delete(customerCode);
    } else {
      newExpanded.add(customerCode);
    }
    setExpandedCustomers(newExpanded);
  };

  // Get customer summary data for a trip
  const getCustomerSummary = (trip: Trip) => {
    const customerMap = new Map<string, { code: string; name: string; shipments: TripShipment[] }>();
    
    trip.shipments.forEach(shipment => {
      const code = shipment.customerCode;
      if (!customerMap.has(code)) {
        customerMap.set(code, {
          code,
          name: shipment.customerName || code,
          shipments: []
        });
      }
      customerMap.get(code)!.shipments.push(shipment);
    });

    // Sort by customer code
    return Array.from(customerMap.values()).sort((a, b) => a.code.localeCompare(b.code));
  };

  // Open customer summary dialog
  const handleOpenCustomerSummary = (trip: Trip) => {
    setSelectedTripForSummary(trip);
    setExpandedCustomers(new Set()); // Reset expanded state
    setIsCustomerSummaryOpen(true);
  };

  // Generate PDF customer summary
  const handlePrintCustomerSummary = (trip: Trip) => {
    const customerSummary = getCustomerSummary(trip);
    const logoUrl = window.location.origin + '/logo-full.jpg';
    
    const totalWeight = trip.shipments.reduce((sum, s) => sum + (s.weight || 0), 0);
    const totalVolume = trip.shipments.reduce((sum, s) => sum + (s.volume || 0), 0);
    const totalPackages = trip.shipments.reduce((sum, s) => sum + (s.packages || 0), 0);
    
    const pdfContent = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8"/>
    <title>Trip Customer Summary - ${trip.name}</title>
    <style>
      * { box-sizing: border-box; margin: 0; padding: 0; }
      @page {
        size: A4 portrait;
        margin: 10mm;
      }
      body {
        font-family: Arial, Helvetica, sans-serif;
        font-size: 9pt;
        line-height: 1.4;
        color: #1a1a2e;
        background: #fff;
        padding: 12px;
      }

      /* HEADER */
      .doc-header {
        display: flex;
        align-items: center;
        gap: 12px;
        padding-bottom: 8px;
        margin-bottom: 12px;
        border-bottom: 2px solid #1e3a6e;
      }
      .doc-header img.logo {
        height: 50px;
        width: auto;
        object-fit: contain;
        flex-shrink: 0;
      }
      .doc-header .vertical-line {
        width: 2px;
        height: 50px;
        background: #1e3a6e;
        flex-shrink: 0;
      }
      .doc-header .company-info {
        flex: 1;
        font-size: 8pt;
        color: #555;
        line-height: 1.5;
      }

      /* TITLE BAND */
      .title-band {
        background: linear-gradient(135deg, #1e3a6e 0%, #2563eb 100%);
        color: #fff;
        padding: 8px 12px;
        border-radius: 4px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;
      }
      .title-band h1 { font-size: 14pt; letter-spacing: 1px; margin: 0; }
      .title-band .meta { text-align: right; font-size: 8pt; line-height: 1.5; }
      .title-band .meta strong { font-size: 10pt; display: block; }

      /* INFO BOXES */
      .info-row {
        display: flex;
        gap: 10px;
        margin-bottom: 12px;
      }
      .info-box {
        flex: 1;
        border: 1px solid #dee2e6;
        border-radius: 4px;
        padding: 8px 10px;
        background: #f8f9fa;
        page-break-inside: avoid;
      }
      .info-box h3 {
        font-size: 8pt;
        text-transform: uppercase;
        color: #1e3a6e;
        border-bottom: 1px solid #1e3a6e;
        padding-bottom: 3px;
        margin-bottom: 6px;
        letter-spacing: 0.5px;
      }
      .info-box p { font-size: 8.5pt; margin-bottom: 2px; }

      /* SECTION TITLE */
      .section-title {
        font-size: 9pt;
        font-weight: bold;
        color: #fff;
        background: linear-gradient(135deg, #1e3a6e 0%, #2563eb 100%);
        padding: 4px 8px;
        border-radius: 3px;
        margin: 12px 0 6px 0;
      }

      /* CUSTOMER SECTION */
      .customer-section {
        margin-bottom: 16px;
        page-break-inside: avoid;
      }
      .customer-header {
        background: #f8f9fa;
        border: 1px solid #dee2e6;
        border-radius: 4px;
        padding: 6px 10px;
        margin-bottom: 6px;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .customer-header h3 {
        font-size: 9.5pt;
        color: #1e3a6e;
        margin: 0;
      }
      .customer-header .customer-stats {
        font-size: 8pt;
        color: #666;
      }

      /* TABLES */
      table {
        width: 100%;
        border-collapse: collapse;
        font-size: 7.5pt;
        page-break-inside: auto;
        margin-bottom: 8px;
      }
      thead tr {
        background: #1e3a6e;
        color: #fff;
      }
      th {
        padding: 5px 6px;
        text-align: left;
        font-weight: 600;
        font-size: 8pt;
      }
      td {
        padding: 4px 6px;
        border-bottom: 1px solid #e9ecef;
      }
      tbody tr:nth-child(even) { background: #f8f9fa; }
      td.right, th.right { text-align: right; }
      tr { page-break-inside: avoid; }

      /* SUBTOTAL ROW */
      .subtotal-row {
        background: #e3f2fd !important;
        font-weight: 600;
        color: #1e3a6e;
      }

      /* FOOTER */
      .doc-footer {
        margin-top: 20px;
        padding-top: 8px;
        border-top: 1px solid #dee2e6;
        text-align: center;
        font-size: 7.5pt;
        color: #888;
        line-height: 1.6;
        page-break-inside: avoid;
      }
      .doc-footer strong { color: #1e3a6e; }

      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    </style>
</head>
<body>

    <!-- HEADER -->
    <div class="doc-header">
      <img class="logo" src="${logoUrl}" alt="SourceMyGoods" onerror="this.style.display='none'"/>
      <div class="vertical-line"></div>
      <div class="company-info">
        Phone: +86 130 4806 4436<br/>
        Email: sales@sourcemygoods.com<br/>
        Address: Office 407, No.6, Yuanjing Road, Tianhe District, Guangzhou, China
      </div>
    </div>

    <!-- TITLE BAND -->
    <div class="title-band">
      <h1>TRIP CUSTOMER SUMMARY</h1>
      <div class="meta">
        <strong>${trip.name}</strong>
        <span>${new Date().toLocaleDateString()}</span>
      </div>
    </div>

    <!-- INFO ROW -->
    <div class="info-row">
      <div class="info-box">
        <h3>Trip Details</h3>
        <p><strong>Trip:</strong> ${trip.name}</p>
        <p><strong>Route:</strong> ${trip.originWarehouse} → ${trip.destinationWarehouse}</p>
        <p><strong>Method:</strong> ${trip.freightMethod || 'Air'}</p>
        <p><strong>Status:</strong> ${trip.status || 'Planning'}</p>
      </div>
      <div class="info-box">
        <h3>Summary</h3>
        <p><strong>Customers:</strong> ${customerSummary.length}</p>
        <p><strong>Shipments:</strong> ${trip.shipments.length}</p>
        <p><strong>Weight:</strong> ${totalWeight.toFixed(2)} kg</p>
        <p><strong>Packages:</strong> ${totalPackages}</p>
      </div>
    </div>

    <!-- CUSTOMER BREAKDOWN -->
    ${customerSummary.map(customer => {
      const customerWeight = customer.shipments.reduce((sum, s) => sum + (s.weight || 0), 0);
      const customerVolume = customer.shipments.reduce((sum, s) => sum + (s.volume || 0), 0);
      const customerPackages = customer.shipments.reduce((sum, s) => sum + (s.packages || 0), 0);
      
      return `
        <div class="customer-section">
          <div class="customer-header">
            <h3>${customer.code} - ${customer.name}</h3>
            <span class="customer-stats">${customer.shipments.length} shipment${customer.shipments.length !== 1 ? 's' : ''}</span>
          </div>
          <table>
            <thead>
              <tr>
                <th>Shipment</th>
                <th class="right">Weight (kg)</th>
                <th class="right">Volume (m³)</th>
                <th class="right">Packages</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${customer.shipments.map(shipment => `
                <tr>
                  <td><strong>${shipment.shipmentNumber}</strong></td>
                  <td class="right">${(shipment.weight || 0).toFixed(2)}</td>
                  <td class="right">${(shipment.volume || 0).toFixed(2)}</td>
                  <td class="right">${shipment.packages || 0}</td>
                  <td>${shipment.status || 'Processing'}</td>
                </tr>
              `).join('')}
              <tr class="subtotal-row">
                <td><strong>Subtotal</strong></td>
                <td class="right"><strong>${customerWeight.toFixed(2)}</strong></td>
                <td class="right"><strong>${customerVolume.toFixed(2)}</strong></td>
                <td class="right"><strong>${customerPackages}</strong></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      `;
    }).join('')}

    <!-- FOOTER -->
    <div class="doc-footer">
      <strong>SourceMyGoods</strong> &nbsp;|&nbsp; Professional Sourcing, Inspection & Global Shipping<br/>
      Operated by Trust Exchange International Trading LTD<br/>
      Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}
    </div>

    <script>window.onload = function() { window.print(); }</script>
</body>
</html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(pdfContent);
      printWindow.document.close();
    }
  };

  const filteredTrips = useMemo(() => {
    return trips.filter(trip => {
      const searchLower = searchTerm.toLowerCase();
      const tripName = (trip.name || '').toLowerCase();
      const originLower = (trip.originWarehouse || '').toLowerCase();
      const destinationLower = (trip.destinationWarehouse || '').toLowerCase();
      const origin = trip.originWarehouse || '';
      const destination = trip.destinationWarehouse || '';
      const driver = (trip.driver || '').toLowerCase();
      const vehicle = (trip.vehicle || '').toLowerCase();
      const status = trip.status || '';
      const freightMethod = trip.freightMethod || 'Sea';

      const searchMatch = (
        tripName.includes(searchLower) ||
        originLower.includes(searchLower) ||
        destinationLower.includes(searchLower) ||
        driver.includes(searchLower) ||
        vehicle.includes(searchLower)
      );

      // Freight method filter
      const freightMatch = freightFilter === 'all' || freightMethod === freightFilter;

      // Warehouse filter (origin or destination) - case sensitive to match warehouse names exactly
      const warehouseMatch = warehouseFilter === 'all' ||
        origin === warehouseFilter ||
        destination === warehouseFilter;

      // Status filter matching based on URL parameter
      let statusMatch = true;
      if (filter === 'planning') {
        statusMatch = status === 'Planning';
      } else if (filter === 'in-transit') {
        statusMatch = status === 'In Transit';
      } else if (filter === 'at-destination') {
        statusMatch = status === 'At Destination';
      } else if (filter === 'completed') {
        statusMatch = status === 'Completed';
      }

      // Date range filter
      let dateMatch = true;
      if (dateRange.startDate && dateRange.endDate && trip.departureDate) {
        const tripDate = new Date(trip.departureDate);
        const start = new Date(dateRange.startDate);
        const end = new Date(dateRange.endDate);
        end.setHours(23, 59, 59, 999); // Include end date fully
        dateMatch = tripDate >= start && tripDate <= end;
      }

      return searchMatch && statusMatch && freightMatch && warehouseMatch && dateMatch;
    }).sort((a, b) => new Date(b.departureDate || '1970-01-01').getTime() - new Date(a.departureDate || '1970-01-01').getTime());
  }, [trips, searchTerm, filter, freightFilter, warehouseFilter, dateRange]);

  const handleAddTrip = () => {
    // Validate required fields
    if (!newTrip.originWarehouse || !newTrip.destinationWarehouse) {
      toast({
        title: 'Error',
        description: 'Please select origin and destination warehouses',
        variant: 'destructive'
      });
      return;
    }

    // Calculate estimated arrival (5 days from departure if date provided)
    let estimatedArrival = null;
    if (newTrip.departureDate) {
      const departureTime = new Date(newTrip.departureDate).getTime();
      const arrivalTime = departureTime + 5 * 24 * 60 * 60 * 1000;
      estimatedArrival = new Date(arrivalTime).toISOString().split('T')[0];
    }

    // Prepare data for API
    const tripData = {
      origin_warehouse: newTrip.originWarehouse,
      destination_warehouse: newTrip.destinationWarehouse,
      freight_method: newTrip.freightMethod,
      status: 'Planning',
      departure_date: newTrip.departureDate || null,
      estimated_arrival: estimatedArrival
    };

    // Call API to create trip
    createTrip(tripData);

    // Reset form and close dialog
    setNewTrip({
      name: '',
      originWarehouse: '',
      destinationWarehouse: '',
      freightMethod: 'Air' as 'Air' | 'Sea',
      departureDate: ''
    });
    setIsAddDialogOpen(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800';
      case 'In Transit': return 'bg-blue-100 text-blue-800';
      case 'At Destination': return 'bg-purple-100 text-purple-800';
      case 'Planning': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Completed': return <CheckCircle className="h-3 w-3" />;
      case 'In Transit': return <Truck className="h-3 w-3" />;
      case 'At Destination': return <Target className="h-3 w-3" />;
      case 'Planning': return <Clock className="h-3 w-3" />;
      default: return <FileText className="h-3 w-3" />;
    }
  };

  const updateTripStatus = (tripId: string, newStatus: Trip['status']) => {
    const trip = allTrips.find(t => t.id === tripId);
    if (!trip) return;

    // Update the trip status
    updateTrip({
      trip_number: trip.name,
      status: newStatus
    });

    // Map trip status to shipment status (excluding Delivered)
    const shipmentStatusMap: Record<Trip['status'], string | null> = {
      'Planning': 'Processing',
      'In Transit': 'In Transit',
      'At Destination': 'At Destination',
      'Completed': null // Don't auto-update when trip completed
    };

    const newShipmentStatus = shipmentStatusMap[newStatus];

    // Update all shipments in this trip (except those already Delivered)
    if (newShipmentStatus && trip.shipments && Array.isArray(trip.shipments) && trip.shipments.length > 0) {
      trip.shipments.forEach((shipment: any) => {
        const shipmentNum = shipment.shipmentNumber || shipment.shipment_number;
        const shipmentStatus = shipment.status;
        
        // Only update if shipment is not already Delivered
        if (shipmentNum && shipmentStatus !== 'Delivered') {
          updateShipment({
            shipment_number: shipmentNum,
            status: newShipmentStatus
          });
        }
      });
    }
  };

  const handleDeleteTrip = (tripId: string) => {
    if (confirm('Are you sure you want to delete this trip? This action cannot be undone.')) {
      const trip = allTrips.find(t => t.id === tripId);
      if (trip) {
        deleteTrip(trip.name);
      }
    }
  };

  const handleEditTrip = (trip: Trip) => {
    setEditingTrip(trip);
    setIsEditDialogOpen(true);
  };

  const handleUpdateTrip = () => {
    if (!editingTrip) return;

    // Find the original trip to check what changed
    const originalTrip = allTrips.find(t => t.id === editingTrip.id);

    // Update the trip
    updateTrip({
      trip_number: editingTrip.name,
      driver: editingTrip.driver,
      vehicle: editingTrip.vehicle,
      origin_warehouse: editingTrip.originWarehouse,
      destination_warehouse: editingTrip.destinationWarehouse,
      freight_method: editingTrip.freightMethod,
      status: editingTrip.status,
      departure_date: editingTrip.departureDate,
      estimated_arrival: editingTrip.estimatedArrival
    });

    // If freight method changed, update all shipments in this trip
    if (originalTrip && originalTrip.freightMethod !== editingTrip.freightMethod) {
      if (editingTrip.shipments && Array.isArray(editingTrip.shipments)) {
        editingTrip.shipments.forEach((shipment: any) => {
          const shipmentNum = shipment.shipmentNumber || shipment.shipment_number;
          if (shipmentNum) {
            updateShipment({
              shipment_number: shipmentNum,
              freight_method: editingTrip.freightMethod
            });
          }
        });
      }
    }

    setIsEditDialogOpen(false);
    setEditingTrip(null);
  };

  // Print Packing List
  const handlePrintPackingList = (trip: Trip) => {
    const destinationWarehouse = warehouses.find(w => w.name === trip.destinationWarehouse);
    const originWarehouse = warehouses.find(w => w.name === trip.originWarehouse);
    const mainWarehouse = warehouses.find(w => w.type === 'Main') || warehouses[0];
    const logoUrl = window.location.origin + '/logo-full.jpg';

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Packing List - ${trip.name}</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            @page {
              size: A4 portrait;
              margin: 10mm;
            }
            body {
              font-family: Arial, Helvetica, sans-serif;
              font-size: 9pt;
              line-height: 1.4;
              color: #1a1a2e;
              background: #fff;
              padding: 12px;
            }

            /* HEADER */
            .doc-header {
              display: flex;
              align-items: center;
              gap: 12px;
              padding-bottom: 8px;
              border-bottom: 2px solid #1e3a6e;
              margin-bottom: 12px;
            }
            .doc-header img.logo {
              height: 50px;
              width: auto;
              object-fit: contain;
              flex-shrink: 0;
            }
            .doc-header .vertical-line {
              width: 2px;
              height: 50px;
              background: #1e3a6e;
              flex-shrink: 0;
            }
            .doc-header .company-info {
              flex: 1;
              font-size: 8pt;
              color: #555;
              line-height: 1.5;
            }
            .doc-header .company-info strong { 
              color: #1e3a6e; 
              font-size: 8.5pt;
            }

            /* TITLE BAND */
            .title-band {
              background: linear-gradient(135deg, #1e3a6e 0%, #2563eb 100%);
              color: #fff;
              padding: 8px 12px;
              border-radius: 4px;
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 12px;
            }
            .title-band h1 { font-size: 14pt; letter-spacing: 1px; margin: 0; }
            .title-band .trip-meta { 
              text-align: right; 
              font-size: 8pt; 
              line-height: 1.5; 
            }
            .title-band .trip-meta strong { 
              font-size: 10pt; 
              display: block; 
            }

            /* INFO BOXES */
            .info-row {
              display: flex;
              gap: 10px;
              margin-bottom: 12px;
            }
            .info-box {
              flex: 1;
              border: 1px solid #dee2e6;
              border-radius: 4px;
              padding: 8px 10px;
              background: #f8f9fa;
              page-break-inside: avoid;
            }
            .info-box h3 {
              font-size: 8pt;
              text-transform: uppercase;
              color: #1e3a6e;
              border-bottom: 1px solid #1e3a6e;
              padding-bottom: 3px;
              margin-bottom: 6px;
              letter-spacing: 0.5px;
            }
            .info-box p { 
              font-size: 8.5pt; 
              margin-bottom: 2px; 
            }

            /* SECTION TITLE */
            .section-title {
              font-size: 9pt;
              font-weight: bold;
              color: #fff;
              background: linear-gradient(135deg, #1e3a6e 0%, #2563eb 100%);
              padding: 4px 8px;
              border-radius: 3px;
              margin: 12px 0 6px 0;
            }

            /* TABLES */
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 7.5pt;
              page-break-inside: auto;
            }
            thead tr {
              background: #1e3a6e;
              color: #fff;
            }
            th {
              padding: 5px 6px;
              text-align: left;
              font-weight: 600;
              font-size: 8pt;
            }
            td {
              padding: 4px 6px;
              border-bottom: 1px solid #e9ecef;
            }
            tbody tr { page-break-inside: avoid; }
            tbody tr:nth-child(even) { background: #f8f9fa; }
            th.right, td.right { text-align: right; }

            /* TOTALS BOX */
            .totals-box {
              margin-top: 12px;
              padding: 10px;
              border: 2px solid #1e3a6e;
              border-radius: 4px;
              background: #f8f9fa;
              max-width: 300px;
              margin-left: auto;
              page-break-inside: avoid;
            }
            .totals-box table { margin: 0; font-size: 8pt; }
            .totals-box tr { border-bottom: 1px solid #dee2e6; }
            .totals-box tr:last-child { border-bottom: none; }
            .totals-box th {
              background: none;
              color: #555;
              padding: 5px 6px;
              font-weight: 600;
              text-align: left;
            }
            .totals-box td {
              padding: 5px 6px;
              border: none;
              text-align: right;
              font-weight: bold;
              color: #1e3a6e;
            }

            /* FOOTER */
            .doc-footer {
              margin-top: 20px;
              padding-top: 8px;
              border-top: 1px solid #dee2e6;
              text-align: center;
              font-size: 7.5pt;
              color: #888;
              line-height: 1.6;
              page-break-inside: avoid;
            }
            .doc-footer p { margin-bottom: 2px; }
            .doc-footer strong { color: #1e3a6e; }

            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>

          <!-- HEADER -->
          <div class="doc-header">
            <img class="logo" src="${logoUrl}" alt="SourceMyGoods" onerror="this.style.display='none'"/>
            <div class="vertical-line"></div>
            <div class="company-info">
              Phone: ${mainWarehouse?.phone || '+86 130 4806 4436'}<br/>
              Email: info@sourcemygoods.com<br/>
              Address: Office 407, No.6, Yuanjing Road, Tianhe District, Guangzhou, China
            </div>
          </div>

          <!-- TITLE BAND -->
          <div class="title-band">
            <h1>PACKING LIST</h1>
            <div class="trip-meta">
              <strong>${trip.name}</strong>
              Freight: ${trip.freightMethod || 'Air'}<br/>
              Date: ${new Date().toLocaleDateString()}
            </div>
          </div>

          <!-- INFO ROW -->
          <div class="info-row">
            <div class="info-box">
              <h3>Origin / Sender</h3>
              <p><strong>${originWarehouse?.name || trip.originWarehouse}</strong></p>
              <p>Address: ${originWarehouse?.address || 'N/A'}</p>
              <p>${originWarehouse?.city || 'N/A'}, ${originWarehouse?.state || 'N/A'}</p>
              <p>${originWarehouse?.country || 'N/A'}</p>
              <p>Phone: ${originWarehouse?.phone || 'N/A'}</p>
              ${originWarehouse?.manager ? `<p>Manager: ${originWarehouse.manager}</p>` : ''}
            </div>

            <div class="info-box">
              <h3>Destination / Receiver</h3>
              <p><strong>${destinationWarehouse?.name || 'Unknown'}</strong></p>
              <p>Address: ${destinationWarehouse?.address || 'N/A'}</p>
              <p>${destinationWarehouse?.city || 'N/A'}, ${destinationWarehouse?.state || 'N/A'}</p>
              <p>${destinationWarehouse?.country || 'N/A'}</p>
              <p>Phone: ${destinationWarehouse?.phone || 'N/A'}</p>
              ${destinationWarehouse?.manager ? `<p>Manager: ${destinationWarehouse.manager}</p>` : ''}
            </div>
          </div>

          <div class="info-row">
            <div class="info-box">
              <h3>Transport Details</h3>
              <p><strong>Driver:</strong> ${trip.driver}</p>
              <p><strong>Vehicle:</strong> ${trip.vehicle}</p>
              <p><strong>Status:</strong> ${trip.status}</p>
            </div>
          </div>

          <!-- SHIPMENT TABLE -->
          <div class="section-title">Shipment Details</div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Shipment No.</th>
                <th>Item Description</th>
                <th class="right">Qty</th>
                <th class="right">Boxes</th>
                <th class="right">Weight (kg)</th>
                <th class="right">Volume (m³)</th>
              </tr>
            </thead>
            <tbody>
              ${(() => {
                let rowNumber = 0;
                return trip.shipments.map((shipment) => {
                  // Only show items if they exist
                  if (shipment.items && shipment.items.length > 0) {
                    return shipment.items.map((item, itemIndex) => {
                      rowNumber++;
                      return `
                      <tr>
                        <td>${rowNumber}</td>
                        <td>${itemIndex === 0 ? shipment.shipmentNumber : ''}</td>
                        <td>${item.description}</td>
                        <td class="right">${item.quantity} ${item.quantityUnit}</td>
                        <td class="right">${item.boxes}</td>
                        <td class="right">${item.weight.toFixed(1)}</td>
                        <td class="right">${item.volume.toFixed(2)}</td>
                      </tr>
                      `;
                    }).join('');
                  }
                  return '';
                }).join('');
              })()}
            </tbody>
          </table>

          <!-- TOTALS BOX -->
          <div class="totals-box">
            <table>
              <tr>
                <th>Total Shipments:</th>
                <td>${trip.shipments.length}</td>
              </tr>
              <tr>
                <th>Total Packages:</th>
                <td>${trip.totalPackages}</td>
              </tr>
              <tr>
                <th>Total Weight:</th>
                <td>${trip.totalWeight.toFixed(2)} kg</td>
              </tr>
              <tr>
                <th>Total Volume:</th>
                <td>${trip.totalVolume.toFixed(2)} m³</td>
              </tr>
            </table>
          </div>

          <!-- FOOTER -->
          <div class="doc-footer">
            <p><strong>SourceMyGoods</strong> &nbsp;|&nbsp; Professional Sourcing, Inspection & Global Shipping</p>
            <p>Operated by Trust Exchange International Trading LTD</p>
            <p>Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
          </div>

          <script>
            window.onload = function() {
              window.print();
            }
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };



  // Print Proforma Invoice
  const handlePrintPerformaInvoice = (trip: Trip) => {
    const destinationWarehouse = warehouses.find(w => w.name === trip.destinationWarehouse);
    const originWarehouse = warehouses.find(w => w.name === trip.originWarehouse);
    const mainWarehouse = warehouses.find(w => w.type === 'Main') || warehouses[0];
    const logoUrl = window.location.origin + '/logo-full.jpg';

    // Prompt user to enter price per piece
    const priceInput = prompt('Enter price per piece ($):', '50');
    if (priceInput === null) return; // User cancelled

    const pricePerPiece = parseFloat(priceInput) || 50;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    // Calculate total from items
    let total = 0;
    trip.shipments.forEach(shipment => {
      if (shipment.items && shipment.items.length > 0) {
        shipment.items.forEach(item => {
          total += item.quantity * pricePerPiece;
        });
      } else {
        total += shipment.packages * pricePerPiece;
      }
    });
    const subtotal = total;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Proforma Invoice - ${trip.name}</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            @page {
              size: A4 portrait;
              margin: 10mm;
            }
            body {
              font-family: Arial, Helvetica, sans-serif;
              font-size: 9pt;
              line-height: 1.4;
              color: #1a1a2e;
              background: #fff;
              padding: 12px;
            }

            /* HEADER */
            .doc-header {
              display: flex;
              align-items: center;
              gap: 12px;
              padding-bottom: 8px;
              margin-bottom: 12px;
              border-bottom: 2px solid #1e3a6e;
            }
            .doc-header img.logo {
              height: 50px;
              width: auto;
              object-fit: contain;
              flex-shrink: 0;
            }
            .doc-header .vertical-line {
              width: 2px;
              height: 50px;
              background: #1e3a6e;
              flex-shrink: 0;
            }
            .doc-header .company-info {
              flex: 1;
              font-size: 8pt;
              color: #555;
              line-height: 1.5;
            }

            /* TITLE BAND */
            .title-band {
              background: linear-gradient(135deg, #1e3a6e 0%, #2563eb 100%);
              color: #fff;
              padding: 8px 12px;
              border-radius: 4px;
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 12px;
            }
            .title-band h1 { font-size: 14pt; letter-spacing: 1px; margin: 0; }
            .title-band .meta {
              text-align: right;
              font-size: 8pt;
              line-height: 1.5;
            }

            /* INFO BOXES */
            .info-row {
              display: flex;
              gap: 10px;
              margin-bottom: 12px;
            }
            .info-box {
              flex: 1;
              border: 1px solid #dee2e6;
              border-radius: 4px;
              padding: 8px 10px;
              background: #f8f9fa;
              page-break-inside: avoid;
            }
            .info-box h3 {
              font-size: 8pt;
              text-transform: uppercase;
              color: #1e3a6e;
              border-bottom: 1px solid #1e3a6e;
              padding-bottom: 3px;
              margin-bottom: 6px;
              letter-spacing: 0.5px;
            }
            .info-box p {
              font-size: 8.5pt;
              margin-bottom: 2px;
            }

            /* SECTION TITLE */
            .section-title {
              font-size: 9pt;
              font-weight: bold;
              color: #fff;
              background: linear-gradient(135deg, #1e3a6e 0%, #2563eb 100%);
              padding: 4px 8px;
              border-radius: 3px;
              margin: 12px 0 6px 0;
            }

            /* TABLES */
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 7.5pt;
              page-break-inside: auto;
            }
            thead tr {
              background: #1e3a6e;
              color: #fff;
            }
            th {
              padding: 5px 6px;
              text-align: left;
              font-weight: 600;
              font-size: 8pt;
            }
            td {
              padding: 4px 6px;
              border-bottom: 1px solid #e9ecef;
            }
            tbody tr { page-break-inside: avoid; }
            tbody tr:nth-child(even) { background: #f8f9fa; }
            th.right, td.right { text-align: right; }

            /* TOTALS BOX */
            .totals-box {
              margin-top: 12px;
              padding: 10px;
              border: 2px solid #1e3a6e;
              border-radius: 4px;
              background: #f8f9fa;
              max-width: 300px;
              margin-left: auto;
              page-break-inside: avoid;
            }
            .totals-box table { margin: 0; font-size: 8pt; }
            .totals-box .total-row {
              background: linear-gradient(135deg, #1e3a6e 0%, #2563eb 100%);
              color: #fff;
            }
            .totals-box .total-row th,
            .totals-box .total-row td {
              padding: 8px;
              font-size: 10pt;
              font-weight: bold;
            }

            /* FOOTER */
            .doc-footer {
              margin-top: 20px;
              padding-top: 8px;
              border-top: 1px solid #dee2e6;
              text-align: center;
              font-size: 7.5pt;
              color: #888;
              line-height: 1.6;
              page-break-inside: avoid;
            }
            .doc-footer p { margin-bottom: 2px; }
            .doc-footer strong { color: #1e3a6e; }
            .doc-footer em {
              display: block;
              margin-top: 8px;
              color: #f5a623;
              font-weight: bold;
              font-style: normal;
            }

            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>

          <!-- HEADER -->
          <div class="doc-header">
            <img class="logo" src="${logoUrl}" alt="SourceMyGoods" onerror="this.style.display='none'"/>
            <div class="vertical-line"></div>
            <div class="company-info">
              Phone: ${mainWarehouse?.phone || '+86 130 4806 4436'}<br/>
              Email: info@sourcemygoods.com<br/>
              Address: Office 407, No.6, Yuanjing Road, Tianhe District, Guangzhou, China
            </div>
          </div>

          <!-- TITLE BAND -->
          <div class="title-band">
            <h1>PROFORMA INVOICE</h1>
            <div class="meta">
              <strong>${trip.name}</strong>
              Freight: ${trip.freightMethod || 'Air'}<br/>
              Date: ${new Date().toLocaleDateString()}
            </div>
          </div>

          <!-- INFO ROW -->
          <div class="info-row">
            <div class="info-box">
              <h3>From / Sender</h3>
              <p><strong>${originWarehouse?.name || trip.originWarehouse}</strong></p>
              <p>Address: ${originWarehouse?.address || 'N/A'}</p>
              <p>${originWarehouse?.city || 'N/A'}, ${originWarehouse?.state || 'N/A'}</p>
              <p>${originWarehouse?.country || 'N/A'}</p>
              <p>Phone: ${originWarehouse?.phone || 'N/A'}</p>
              ${originWarehouse?.manager ? `<p>Manager: ${originWarehouse.manager}</p>` : ''}
            </div>

            <div class="info-box">
              <h3>Bill To / Receiver</h3>
              <p><strong>${destinationWarehouse?.name || 'Unknown'}</strong></p>
              <p>Address: ${destinationWarehouse?.address || 'N/A'}</p>
              <p>${destinationWarehouse?.city || 'N/A'}, ${destinationWarehouse?.state || 'N/A'}</p>
              <p>${destinationWarehouse?.country || 'N/A'}</p>
              <p>Phone: ${destinationWarehouse?.phone || 'N/A'}</p>
              ${destinationWarehouse?.manager ? `<p>Manager: ${destinationWarehouse.manager}</p>` : ''}
            </div>
          </div>

          <!-- SHIPMENT TABLE -->
          <div class="section-title">Shipment Details</div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Shipment No.</th>
                <th>Item Description</th>
                <th class="right">Qty</th>
                <th class="right">Boxes</th>
                <th class="right">Weight (kg)</th>
                <th class="right">Price (per piece)</th>
                <th class="right">Amount ($)</th>
              </tr>
            </thead>
            <tbody>
              ${(() => {
                let rowNumber = 0;
                return trip.shipments.map((shipment) => {
                  // Only show items if they exist
                  if (shipment.items && shipment.items.length > 0) {
                    return shipment.items.map((item, itemIndex) => {
                      rowNumber++;
                      const amount = item.quantity * pricePerPiece;
                      return `
                      <tr>
                        <td>${rowNumber}</td>
                        <td>${itemIndex === 0 ? shipment.shipmentNumber : ''}</td>
                        <td>${item.description}</td>
                        <td class="right">${item.quantity} ${item.quantityUnit}</td>
                        <td class="right">${item.boxes}</td>
                        <td class="right">${item.weight.toFixed(1)}</td>
                        <td class="right">${pricePerPiece.toFixed(2)}</td>
                        <td class="right">${amount.toFixed(2)}</td>
                      </tr>
                      `;
                    }).join('');
                  }
                  return '';
                }).join('');
              })()}
            </tbody>
          </table>

          <!-- SHIPMENT TOTALS BOX -->
          <div class="totals-box">
            <table>
              <tr>
                <th>Total Shipments:</th>
                <td>${trip.shipments.length}</td>
              </tr>
              <tr>
                <th>Total Packages:</th>
                <td>${trip.totalPackages}</td>
              </tr>
              <tr>
                <th>Total Weight:</th>
                <td>${trip.totalWeight.toFixed(2)} kg</td>
              </tr>
              <tr>
                <th>Total Volume:</th>
                <td>${trip.totalVolume.toFixed(2)} m³</td>
              </tr>
            </table>
          </div>

          <!-- FINANCIAL TOTAL BOX -->
          <div class="totals-box">
            <table>
              <tr class="total-row">
                <th>TOTAL AMOUNT</th>
                <td class="right">$ ${total.toFixed(2)}</td>
              </tr>
            </table>
          </div>

          <!-- FOOTER -->
          <div class="doc-footer">
            <p><strong>SourceMyGoods</strong> &nbsp;|&nbsp; Professional Sourcing, Inspection & Global Shipping</p>
            <p>Operated by Trust Exchange International Trading LTD</p>
            <p>Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
            <em>This is a proforma invoice and not a tax invoice.</em>
          </div>

          <script>
            window.onload = function() {
              window.print();
            }
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {filter === 'planning' ? 'Planning Trips' :
               filter === 'in-transit' ? 'In Transit Trips' :
               filter === 'at-destination' ? 'At Destination Trips' :
               filter === 'completed' ? 'Completed Trips' :
               'Trips'}
            </h1>
            <p className="text-gray-600">
              {filter === 'planning' ? 'View all trips that are currently being planned' :
               filter === 'in-transit' ? 'View all trips that are currently in transit' :
               filter === 'at-destination' ? 'View all trips that have arrived at destination' :
               filter === 'completed' ? 'View all trips that have been completed' :
               'Manage trips and automatically calculate totals for assigned shipments'}
            </p>
          </div>

          {user?.role !== 'customer' && (
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Trip
                </Button>
              </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Create New Trip</DialogTitle>
                <DialogDescription>
                  Set up a new trip for grouping shipments
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Trip Name</Label>
                  <Input
                    id="name"
                    value={newTrip.name}
                    onChange={(e) => setNewTrip({...newTrip, name: e.target.value})}
                    placeholder="e.g., East Coast Express"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="origin">Origin Warehouse</Label>
                    <Select value={newTrip.originWarehouse} onValueChange={(value) => setNewTrip({...newTrip, originWarehouse: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select origin" />
                      </SelectTrigger>
                      <SelectContent>
                        {warehouses.map(warehouse => (
                          <SelectItem key={warehouse.code} value={warehouse.code}>
                            {warehouse.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="destination">Destination Warehouse</Label>
                    <Select value={newTrip.destinationWarehouse} onValueChange={(value) => setNewTrip({...newTrip, destinationWarehouse: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select destination" />
                      </SelectTrigger>
                      <SelectContent>
                        {warehouses.map(warehouse => (
                          <SelectItem key={warehouse.code} value={warehouse.code}>
                            {warehouse.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="freightMethod">Freight Method</Label>
                    <Select value={newTrip.freightMethod} onValueChange={(value) => setNewTrip({...newTrip, freightMethod: value as 'Air' | 'Sea'})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Air">Air Freight</SelectItem>
                        <SelectItem value="Sea">Sea Freight</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">Choose the transportation method for this trip</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="departureDate">Departure Date</Label>
                    <Input
                      id="departureDate"
                      type="date"
                      value={newTrip.departureDate}
                      onChange={(e) => setNewTrip({...newTrip, departureDate: e.target.value})}
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleAddTrip}>Create Trip</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          )}
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search trips by name or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* View Toggle */}
          <div className="flex border rounded-md">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="rounded-r-none"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="rounded-l-none"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="w-full sm:w-auto"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
            className="w-full sm:w-auto"
          >
            <Calendar className="h-4 w-4 mr-2" />
            History
          </Button>
        </div>

        {/* Filter Dropdowns */}
        {showFilters && (
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Warehouse Filter */}
                <div className="space-y-2">
                  <Label htmlFor="warehouse-filter">Filter by Warehouse</Label>
                  <Select value={warehouseFilter} onValueChange={setWarehouseFilter}>
                    <SelectTrigger id="warehouse-filter">
                      <SelectValue placeholder="All Warehouses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Warehouses</SelectItem>
                      {warehouses.map(warehouse => (
                        <SelectItem key={warehouse.code} value={warehouse.name}>
                          {warehouse.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Freight Method Filter */}
                <div className="space-y-2">
                  <Label htmlFor="freight-filter">Filter by Freight Method</Label>
                  <Select value={freightFilter} onValueChange={setFreightFilter}>
                    <SelectTrigger id="freight-filter">
                      <SelectValue placeholder="All Methods" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Methods</SelectItem>
                      <SelectItem value="Air">Air Freight</SelectItem>
                      <SelectItem value="Sea">Sea Freight</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Date Range History */}
        {showHistory && (
          <Card>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Date Range History</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowHistory(false)}
                >
                  ✕
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startDate">Start Date</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
                    placeholder="Select start date"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endDate">End Date</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
                    placeholder="Select end date"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    if (dateRange.startDate && dateRange.endDate) {
                      // Filter trips by date range
                      const filtered = filteredTrips.filter(trip => {
                        const tripDate = new Date(trip.departureDate);
                        const start = new Date(dateRange.startDate);
                        const end = new Date(dateRange.endDate);
                        end.setHours(23, 59, 59, 999); // Include end date fully
                        return tripDate >= start && tripDate <= end;
                      });
                      alert(`Found ${filtered.length} trips between ${dateRange.startDate} and ${dateRange.endDate}`);
                    } else {
                      alert('Please select both start and end dates');
                    }
                  }}
                  className="flex-1"
                >
                  Apply Date Filter
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setDateRange({ startDate: '', endDate: '' });
                    setShowHistory(false);
                  }}
                >
                  Clear
                </Button>
              </div>
              {/* Summary Statistics */}
              {dateRange.startDate && dateRange.endDate && (
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium mb-3">Summary Statistics</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {['Planning', 'In Transit', 'At Destination', 'Completed'].map(status => {
                      const count = filteredTrips.filter(t => t.status === status).length;
                      return (
                        <div key={status} className="text-center p-3 bg-white rounded border">
                          <div className="text-2xl font-bold text-gray-900">{count}</div>
                          <div className="text-sm text-gray-600">{status}</div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Additional Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div className="text-center p-3 bg-blue-50 rounded border border-blue-200">
                      <div className="text-xl font-bold text-blue-900">
                        {filteredTrips.reduce((sum, t) => sum + (t.totalWeight || 0), 0).toFixed(1)}
                      </div>
                      <div className="text-sm text-blue-700">Total Weight (kg)</div>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded border border-green-200">
                      <div className="text-xl font-bold text-green-900">
                        {filteredTrips.reduce((sum, t) => sum + (t.totalPackages || 0), 0)}
                      </div>
                      <div className="text-sm text-green-700">Total Packages</div>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded border border-purple-200">
                      <div className="text-xl font-bold text-purple-900">
                        {filteredTrips.length}
                      </div>
                      <div className="text-sm text-purple-700">Total Trips</div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Trip Cards */}
        {tripsLoading ? (
          <div className="grid grid-cols-1 gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="border-l-4 border-l-indigo-500">
                <CardHeader>
                  <Skeleton className="h-6 w-1/3 mb-2" />
                  <Skeleton className="h-4 w-1/2" />
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="grid grid-cols-4 gap-4">
                      <Skeleton className="h-16 w-full" />
                      <Skeleton className="h-16 w-full" />
                      <Skeleton className="h-16 w-full" />
                      <Skeleton className="h-16 w-full" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredTrips.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center">
              <Truck className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Trips Found</h3>
              <p className="text-gray-500 mb-6">
                {searchTerm ? 'Try adjusting your search criteria' : 'Create your first trip to start organizing shipments'}
              </p>
              {user?.role !== 'customer' && (
                <Button onClick={() => setIsAddDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Trip
                </Button>
              )}
            </CardContent>
          </Card>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 gap-4">
            {filteredTrips.map((trip, index) => (
            <Card 
              key={trip.id} 
              className="hover:shadow-xl transition-all duration-300 border-l-4 border-l-indigo-500 hover:border-blue-300 hover:scale-[1.01]"
              style={{ animationDelay: `${index * 30}ms` }}
            >
              <CardHeader className="pb-3">
                <div className="flex flex-col lg:flex-row lg:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                      {trip.freightMethod === 'Air' ? (
                        <Plane className="h-5 w-5 text-blue-600 flex-shrink-0" />
                      ) : (
                        <Ship className="h-5 w-5 text-green-600 flex-shrink-0" />
                      )}
                      <CardTitle className="text-lg break-words">{trip.name}</CardTitle>
                      <CopyButton text={trip.name} label="Trip Name" size="icon" />
                      <Badge className={`${getStatusColor(trip.status)} whitespace-nowrap flex items-center gap-1`}>
                        {getStatusIcon(trip.status)}
                        {trip.status}
                      </Badge>
                    </div>
                    <CardDescription className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span className="break-words">{trip.originWarehouse} → {trip.destinationWarehouse}</span>
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {user?.role !== 'customer' && (
                      <>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => {
                            setSelectedTripForShipment(trip);
                            setIsCreateShipmentOpen(true);
                          }}
                          title="Create Shipment for this Trip"
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Package className="h-4 w-4 mr-1" />
                          + Shipment
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => {
                            setSelectedTripForInvoice(trip);
                            setIsCreateInvoiceOpen(true);
                          }}
                          title="Create Invoice for this Trip"
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <Receipt className="h-4 w-4 mr-1" />
                          + Invoice
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenCustomerSummary(trip)}
                          title="View Customer Summary"
                        >
                          <Users className="h-4 w-4 mr-1" />
                          Customers
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePrintPackingList(trip)}
                          title="Print Packing List"
                        >
                          <FileText className="h-4 w-4 mr-1" />
                          Packing
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePrintPerformaInvoice(trip)}
                          title="Print Proforma Invoice"
                        >
                          <Printer className="h-4 w-4 mr-1" />
                          Proforma
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditTrip(trip)}
                          title="Edit Trip"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteTrip(trip.id)}
                          title="Delete Trip"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        <Select value={trip.status} onValueChange={(value) => updateTripStatus(trip.id, value as Trip['status'])}>
                          <SelectTrigger className="w-[130px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Planning">Planning</SelectItem>
                            <SelectItem value="In Transit">In Transit</SelectItem>
                            <SelectItem value="At Destination">At Destination</SelectItem>
                            <SelectItem value="Completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                      </>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleTripExpansion(trip.id)}
                    >
                      {expandedTrips.has(trip.id) ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{trip.shipments.length}</div>
                    <div className="text-xs text-gray-500 flex items-center justify-center mt-1">
                      <Package className="h-3 w-3 mr-1" />
                      Shipments
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{trip.totalWeight.toFixed(1)}</div>
                    <div className="text-xs text-gray-500 flex items-center justify-center mt-1">
                      <Weight className="h-3 w-3 mr-1" />
                      Total kg
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{trip.totalVolume.toFixed(1)}</div>
                    <div className="text-xs text-gray-500 flex items-center justify-center mt-1">
                      <Box className="h-3 w-3 mr-1" />
                      Total m³
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{trip.totalPackages}</div>
                    <div className="text-xs text-gray-500 flex items-center justify-center mt-1">
                      <Package className="h-3 w-3 mr-1" />
                      Total Packages
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-medium text-gray-900">{trip.driver}</div>
                    <div className="text-xs text-gray-500 mt-1">{trip.vehicle}</div>
                  </div>
                </div>

                {/* Trip Details */}
                <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                  <div className="flex items-center text-gray-600">
                    <Calendar className="h-4 w-4 mr-2" />
                    <span>Departure: {trip.departureDate}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Calendar className="h-4 w-4 mr-2" />
                    <span>Arrival: {trip.estimatedArrival}</span>
                  </div>
                </div>

                {/* Shipments List (Collapsible) */}
                {expandedTrips.has(trip.id) && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-3 text-sm text-gray-700">Shipments in this trip:</h4>
                    <div className="space-y-2">
                      {trip.shipments.map((shipment, index) => (
                        <div key={index} className="flex flex-wrap sm:flex-nowrap justify-between items-center p-3 bg-white border rounded-lg gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <Package className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <div className="min-w-0">
                              <div className="font-medium text-sm truncate">{shipment.shipmentNumber}</div>
                              <div className="text-xs text-gray-500 truncate">{shipment.customerCode}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 sm:gap-6 text-sm text-gray-600 flex-shrink-0">
                            <div className="flex items-center whitespace-nowrap">
                              <Weight className="h-3 w-3 mr-1" />
                              {shipment.weight} kg
                            </div>
                            <div className="flex items-center whitespace-nowrap">
                              <Box className="h-3 w-3 mr-1" />
                              {shipment.volume} m³
                            </div>
                            <div className="flex items-center whitespace-nowrap">
                              <Package className="h-3 w-3 mr-1" />
                              {shipment.packages} pkgs
                            </div>
                            <Badge className="text-xs" variant="outline">
                              {shipment.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
        ) : (
          /* List View */
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trip</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Route</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transport</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredTrips.map((trip) => (
                      <tr key={trip.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{trip.name}</div>
                          <div className="text-sm text-gray-500">{trip.freightMethod} Freight</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-900">{trip.origin}</div>
                          <div className="text-xs text-gray-500">→ {trip.destinationWarehouse}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{trip.driver}</div>
                          <div className="text-sm text-gray-500">{trip.vehicle}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{trip.shipments.length} shipments</div>
                          <div className="text-sm text-gray-500">{trip.totalWeight} kg • {trip.totalPackages} pkgs</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <Badge className={
                            trip.status === 'Planning' ? 'bg-gray-100 text-gray-800' :
                            trip.status === 'In Transit' ? 'bg-blue-100 text-blue-800' :
                            trip.status === 'At Destination' ? 'bg-purple-100 text-purple-800' :
                            'bg-green-100 text-green-800'
                          }>
                            {trip.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePrintPackingList(trip)}
                              title="Print Packing List"
                            >
                              <Printer className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingTrip(trip);
                                setIsEditDialogOpen(true);
                              }}
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTrip(trip.id)}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

      {/* Edit Trip Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Trip</DialogTitle>
            <DialogDescription>
              Update trip details and information
            </DialogDescription>
          </DialogHeader>
          {editingTrip && (
            <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="editTripName">Trip Name</Label>
                  <Input
                    id="editTripName"
                    value={editingTrip.name}
                    onChange={(e) => setEditingTrip({...editingTrip, name: e.target.value})}
                    placeholder="Enter trip name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editFreightMethod">Freight Method</Label>
                  <Select
                    value={editingTrip.freightMethod}
                    onValueChange={(value: 'Air' | 'Sea') => setEditingTrip({...editingTrip, freightMethod: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Air">Air</SelectItem>
                      <SelectItem value="Sea">Sea</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="editOriginWarehouse">Origin Warehouse</Label>
                  <Input
                    id="editOriginWarehouse"
                    value={editingTrip.originWarehouse}
                    onChange={(e) => setEditingTrip({...editingTrip, originWarehouse: e.target.value})}
                    placeholder="Enter origin warehouse"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editDestinationWarehouse">Destination Warehouse</Label>
                  <Input
                    id="editDestinationWarehouse"
                    value={editingTrip.destinationWarehouse}
                    onChange={(e) => setEditingTrip({...editingTrip, destinationWarehouse: e.target.value})}
                    placeholder="Enter destination warehouse"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="editDriver">Driver</Label>
                  <Input
                    id="editDriver"
                    value={editingTrip.driver}
                    onChange={(e) => setEditingTrip({...editingTrip, driver: e.target.value})}
                    placeholder="Enter driver name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editVehicle">Vehicle</Label>
                  <Input
                    id="editVehicle"
                    value={editingTrip.vehicle}
                    onChange={(e) => setEditingTrip({...editingTrip, vehicle: e.target.value})}
                    placeholder="Enter vehicle number"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="editDepartureDate">Departure Date</Label>
                  <Input
                    id="editDepartureDate"
                    type="date"
                    value={editingTrip.departureDate}
                    onChange={(e) => setEditingTrip({...editingTrip, departureDate: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editEstimatedArrival">Estimated Arrival</Label>
                  <Input
                    id="editEstimatedArrival"
                    type="date"
                    value={editingTrip.estimatedArrival}
                    onChange={(e) => setEditingTrip({...editingTrip, estimatedArrival: e.target.value})}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="editStatus">Status</Label>
                <Select
                  value={editingTrip.status}
                  onValueChange={(value: Trip['status']) => setEditingTrip({...editingTrip, status: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Planning">Planning</SelectItem>
                    <SelectItem value="In Transit">In Transit</SelectItem>
                    <SelectItem value="At Destination">At Destination</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Trip Summary</Label>
                <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <div className="text-xl font-bold text-gray-900">{editingTrip.shipments.length}</div>
                    <div className="text-xs text-gray-500">Shipments</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-gray-900">{editingTrip.totalWeight.toFixed(1)}</div>
                    <div className="text-xs text-gray-500">Total kg</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-gray-900">{editingTrip.totalPackages}</div>
                    <div className="text-xs text-gray-500">Packages</div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateTrip}>
              Update Trip
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Summary Dialog */}
      <Dialog open={isCustomerSummaryOpen} onOpenChange={setIsCustomerSummaryOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Customer Summary
            </DialogTitle>
            <DialogDescription>
              {selectedTripForSummary && (
                <span>
                  Trip: <strong>{selectedTripForSummary.name}</strong> | 
                  {selectedTripForSummary.originWarehouse} → {selectedTripForSummary.destinationWarehouse}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {selectedTripForSummary && (
            <div className="space-y-3 py-4">
              {/* Trip Summary Stats */}
              <div className="grid grid-cols-4 gap-3 p-3 bg-gray-50 rounded-lg text-center">
                <div>
                  <div className="text-lg font-bold text-gray-900">
                    {getCustomerSummary(selectedTripForSummary).length}
                  </div>
                  <div className="text-xs text-gray-500">Customers</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-gray-900">
                    {selectedTripForSummary.shipments.length}
                  </div>
                  <div className="text-xs text-gray-500">Shipments</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-gray-900">
                    {selectedTripForSummary.totalWeight.toFixed(1)}
                  </div>
                  <div className="text-xs text-gray-500">Total kg</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-gray-900">
                    {selectedTripForSummary.totalPackages}
                  </div>
                  <div className="text-xs text-gray-500">Packages</div>
                </div>
              </div>

              {/* Customer List */}
              <div className="space-y-2">
                {getCustomerSummary(selectedTripForSummary).length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No shipments in this trip yet
                  </div>
                ) : (
                  getCustomerSummary(selectedTripForSummary).map((customer) => (
                    <div key={customer.code} className="border rounded-lg overflow-hidden">
                      {/* Customer Header - Clickable */}
                      <button
                        className="w-full flex items-center justify-between p-3 bg-white hover:bg-gray-50 transition-colors"
                        onClick={() => toggleCustomerExpansion(customer.code)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                            <span className="text-sm font-bold text-indigo-600">
                              {customer.code.slice(-2)}
                            </span>
                          </div>
                          <div className="text-left">
                            <div className="font-medium text-gray-900">
                              {customer.code} - {customer.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {customer.shipments.length} shipment{customer.shipments.length !== 1 ? 's' : ''} | 
                              {' '}{customer.shipments.reduce((sum, s) => sum + s.weight, 0).toFixed(1)} kg | 
                              {' '}{customer.shipments.reduce((sum, s) => sum + s.packages, 0)} pkgs
                            </div>
                          </div>
                        </div>
                        {expandedCustomers.has(customer.code) ? (
                          <ChevronUp className="h-4 w-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-400" />
                        )}
                      </button>

                      {/* Customer Shipments - Expandable */}
                      {expandedCustomers.has(customer.code) && (
                        <div className="border-t bg-gray-50 p-2 space-y-2">
                          {customer.shipments.map((shipment, idx) => (
                            <div
                              key={idx}
                              className="flex items-center justify-between p-2 bg-white rounded border text-sm"
                            >
                              <div className="flex items-center gap-2">
                                <Package className="h-4 w-4 text-gray-400" />
                                <span className="font-medium">{shipment.shipmentNumber}</span>
                              </div>
                              <div className="flex items-center gap-4 text-gray-600">
                                <span className="flex items-center gap-1">
                                  <Weight className="h-3 w-3" />
                                  {shipment.weight} kg
                                </span>
                                <span className="flex items-center gap-1">
                                  <Box className="h-3 w-3" />
                                  {shipment.volume} m³
                                </span>
                                <span className="flex items-center gap-1">
                                  <Package className="h-3 w-3" />
                                  {shipment.packages} pkgs
                                </span>
                                <Badge variant="outline" className="text-xs">
                                  {shipment.status}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => selectedTripForSummary && handlePrintCustomerSummary(selectedTripForSummary)}
              className="mr-auto"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print PDF
            </Button>
            <Button variant="outline" onClick={() => setIsCustomerSummaryOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Shipment Dialog */}
      <Dialog open={isCreateShipmentOpen} onOpenChange={setIsCreateShipmentOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Shipment for Trip</DialogTitle>
            <DialogDescription>
              {selectedTripForShipment && (
                <span>
                  Trip: <strong>{selectedTripForShipment.name}</strong> | 
                  {selectedTripForShipment.originWarehouse} → {selectedTripForShipment.destinationWarehouse}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {selectedTripForShipment && (
            <MultiItemShipmentForm
              customers={apiCustomers || []}
              trips={apiTrips || []}
              warehouses={apiWarehouses || []}
              onSubmit={(data) => {
                createShipment(data);
                setIsCreateShipmentOpen(false);
              }}
              isSubmitting={isCreatingShipment}
              showTripSelection={true}
              defaultStatus="Processing"
              initialData={{
                originWarehouse: selectedTripForShipment.originWarehouse,
                destinationWarehouse: selectedTripForShipment.destinationWarehouse,
                tripName: selectedTripForShipment.name,
                freightMethod: selectedTripForShipment.freightMethod
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Create Invoice Dialog */}
      <Dialog open={isCreateInvoiceOpen} onOpenChange={(open) => {
        setIsCreateInvoiceOpen(open);
        if (!open) {
          // Reset form when closing
          setSelectedCustomerForInvoice('');
          setSelectedShipmentsForInvoice(new Set());
          setInvoiceRate('10');
          setInvoiceDiscount('0');
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Invoice for Trip</DialogTitle>
            <DialogDescription>
              {selectedTripForInvoice && (
                <span>
                  Trip: <strong>{selectedTripForInvoice.name}</strong> | 
                  Destination: {selectedTripForInvoice.destinationWarehouse}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {selectedTripForInvoice && (() => {
            // Get shipments for this trip
            const tripShipments = selectedTripForInvoice.shipments || [];
            
            // Get unique customers from trip shipments
            const uniqueCustomers = Array.from(
              new Set(tripShipments.map(s => s.customerCode))
            ).map(code => {
              const shipment = tripShipments.find(s => s.customerCode === code);
              return {
                code,
                name: shipment?.customerName || code
              };
            });
            
            // Filter shipments by selected customer
            const customerShipments = selectedCustomerForInvoice
              ? tripShipments.filter(s => s.customerCode === selectedCustomerForInvoice)
              : [];
            
            // Get selected shipment data for calculation
            const selectedShipmentData = customerShipments.filter(s =>
              selectedShipmentsForInvoice.has(s.shipmentNumber)
            );
            
            // Calculate totals
            const rate = parseFloat(invoiceRate) || 0;
            const isAir = selectedTripForInvoice.freightMethod === 'Air';
            const subtotal = selectedShipmentData.reduce((sum, s) => {
              const amount = isAir ? (s.weight * rate) : (s.volume * rate);
              return sum + amount;
            }, 0);
            const discount = parseFloat(invoiceDiscount) || 0;
            const total = subtotal - discount;
            
            return (
              <div className="space-y-4 py-4">
                {/* Step 1: Select Customer */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Step 1: Select Customer</Label>
                  <Select value={selectedCustomerForInvoice} onValueChange={setSelectedCustomerForInvoice}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a customer..." />
                    </SelectTrigger>
                    <SelectContent>
                      {uniqueCustomers.map(customer => (
                        <SelectItem key={customer.code} value={customer.code}>
                          {customer.code} - {customer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedCustomerForInvoice && (
                  <>
                    {/* Step 2: Select Shipments */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        Step 2: Select Shipments ({selectedShipmentsForInvoice.size} selected)
                      </Label>
                      <div className="border rounded-lg max-h-60 overflow-y-auto">
                        {customerShipments.length === 0 ? (
                          <div className="p-4 text-sm text-gray-500 text-center">
                            No shipments found for this customer on this trip
                          </div>
                        ) : (
                          <div className="divide-y">
                            {customerShipments.map(shipment => (
                              <div key={shipment.shipmentNumber} className="flex items-center p-3 hover:bg-gray-50">
                                <Checkbox
                                  checked={selectedShipmentsForInvoice.has(shipment.shipmentNumber)}
                                  onCheckedChange={(checked) => {
                                    const newSelected = new Set(selectedShipmentsForInvoice);
                                    if (checked) {
                                      newSelected.add(shipment.shipmentNumber);
                                    } else {
                                      newSelected.delete(shipment.shipmentNumber);
                                    }
                                    setSelectedShipmentsForInvoice(newSelected);
                                  }}
                                />
                                <div className="ml-3 flex-1">
                                  <div className="text-sm font-medium">{shipment.shipmentNumber}</div>
                                  <div className="text-xs text-gray-500">
                                    {shipment.weight} kg • {shipment.volume} m³ • {shipment.packages} pkgs
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {selectedShipmentsForInvoice.size > 0 && (
                      <>
                        {/* Step 3: Set Rate & Discount */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-sm font-medium">
                              Rate per {isAir ? 'KG' : 'CBM'} ($)
                            </Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={invoiceRate}
                              onChange={(e) => setInvoiceRate(e.target.value)}
                              placeholder="10.00"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm font-medium">Discount ($)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={invoiceDiscount}
                              onChange={(e) => setInvoiceDiscount(e.target.value)}
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                        
                        {/* Summary */}
                        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Subtotal:</span>
                            <span className="font-medium">${subtotal.toFixed(2)}</span>
                          </div>
                          {discount > 0 && (
                            <div className="flex justify-between text-sm text-green-600">
                              <span>Discount:</span>
                              <span>-${discount.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-base font-bold border-t pt-2">
                            <span>Total:</span>
                            <span>${total.toFixed(2)}</span>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            );
          })()}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateInvoiceOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!selectedTripForInvoice || !selectedCustomerForInvoice || selectedShipmentsForInvoice.size === 0) {
                  toast({
                    title: 'Error',
                    description: 'Please select customer and at least one shipment',
                    variant: 'destructive'
                  });
                  return;
                }
                
                const customer = (apiCustomers || []).find((c: any) => c.code === selectedCustomerForInvoice);
                const rate = parseFloat(invoiceRate) || 0;
                const discount = parseFloat(invoiceDiscount) || 0;
                const isAir = selectedTripForInvoice.freightMethod === 'Air';
                
                // Get selected shipments
                const selectedShipmentNumbers = Array.from(selectedShipmentsForInvoice);
                
                // Calculate subtotal
                const tripShipments = selectedTripForInvoice.shipments || [];
                const selectedShipmentData = tripShipments.filter(s =>
                  selectedShipmentsForInvoice.has(s.shipmentNumber)
                );
                const subtotal = selectedShipmentData.reduce((sum, s) => {
                  const amount = isAir ? (s.weight * rate) : (s.volume * rate);
                  return sum + amount;
                }, 0);
                const total = subtotal - discount;
                
                // Get today's date and due date (30 days from now)
                const today = new Date();
                const dueDate = new Date(today);
                dueDate.setDate(dueDate.getDate() + 30);
                
                const invoiceData = {
                  customer_code: selectedCustomerForInvoice,
                  customer_name: customer?.name || selectedCustomerForInvoice,
                  customer_phone: customer?.phone || '',
                  customer_email: customer?.email || '',
                  customer_address: customer?.address || '',
                  destination_warehouse: selectedTripForInvoice.destinationWarehouse,
                  trip_name: selectedTripForInvoice.name,
                  shipments: selectedShipmentNumbers, // API expects 'shipments' not 'shipment_numbers'
                  rate_per_kg: rate,
                  subtotal: subtotal,
                  tax: discount, // Using tax field for discount
                  discount: discount,
                  total: total,
                  balance_due: total, // Set balance_due equal to total (unpaid)
                  status: 'Draft',
                  issue_date: today.toISOString().split('T')[0],
                  due_date: dueDate.toISOString().split('T')[0]
                };
                
                createInvoice(invoiceData);
                setIsCreateInvoiceOpen(false);
                // Reset form
                setSelectedCustomerForInvoice('');
                setSelectedShipmentsForInvoice(new Set());
                setInvoiceRate('10');
                setInvoiceDiscount('0');
              }}
              disabled={!selectedCustomerForInvoice || selectedShipmentsForInvoice.size === 0 || isCreatingInvoice}
            >
              {isCreatingInvoice ? 'Creating...' : 'Create Invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </Layout>
  );
}